import { useCallback, useRef, useState } from 'react';

type CaptchaForm = {
  recaptcha_response_v2?: string;
  recaptcha_response_v3?: string;
};

type Options = {
  clearSubmissionErrors: () => void;
  hasV2Token: () => boolean;
  resetV2Token: () => void;
  setGenericError: () => void;
  setRequiredV2Error: () => void;
};

export const useCaptchaFallback = ({
  clearSubmissionErrors,
  hasV2Token,
  resetV2Token,
  setGenericError,
  setRequiredV2Error,
}: Options) => {
  const [useV2, setUseV2] = useState(false);
  const [shouldFocusV2, setShouldFocusV2] = useState(false);
  const submittedWithV2 = useRef(false);

  const handleV2Focus = useCallback(() => setShouldFocusV2(false), []);

  const handleCaptchaError = (field: PropertyKey): boolean => {
    if (field === 'recaptcha_response_v3') {
      setUseV2(true);
      setShouldFocusV2(true);
      return true;
    }
    if (field === 'recaptcha_response_v2') {
      resetV2Token();
      setGenericError();
      setShouldFocusV2(true);
      return true;
    }
    return false;
  };

  const prepareCaptchaSubmission = <Form extends CaptchaForm>(
    data: Form,
  ): (Form & { recaptcha_response_v2: string | undefined }) | null => {
    clearSubmissionErrors();
    if (useV2 && !data.recaptcha_response_v2) {
      setRequiredV2Error();
      setShouldFocusV2(true);
      return null;
    }
    if (!data.recaptcha_response_v2 && !data.recaptcha_response_v3) {
      setUseV2(true);
      setGenericError();
      setShouldFocusV2(true);
      return null;
    }

    const recaptchaResponseV2 = data.recaptcha_response_v2 || undefined;
    submittedWithV2.current = !!recaptchaResponseV2;
    if (recaptchaResponseV2) {
      resetV2Token();
    }
    return { ...data, recaptcha_response_v2: recaptchaResponseV2 };
  };

  const handleSubmissionError = () => {
    if (submittedWithV2.current && !hasV2Token()) {
      setShouldFocusV2(true);
    }
    submittedWithV2.current = false;
  };

  return {
    handleCaptchaError,
    handleSubmissionError,
    handleV2Focus,
    prepareCaptchaSubmission,
    shouldFocusV2,
    useV2,
  };
};
